import { Controller } from "@hotwired/stimulus";

/**
 * Keeps a check-in/check-out date pair within a valid range in the browser,
 * mirroring the server-side validation in NuAccommodationBooking: check-in
 * must be in the future, and check-out must be after check-in.
 *
 * Usage:
 *   <div data-controller="forms--date-range">
 *     <input type="date" data-forms--date-range-target="checkIn"
 *            data-action="change->forms--date-range#checkInChanged">
 *     <input type="date" data-forms--date-range-target="checkOut">
 *   </div>
 */
export default class extends Controller {
  static targets = ["checkIn", "checkOut"];

  connect() {
    this.checkInTarget.min = this.tomorrowISO();
    this.updateCheckOutMin();
  }

  checkInChanged() {
    this.updateCheckOutMin();

    if (
      this.checkOutTarget.value &&
      this.checkOutTarget.value <= this.checkInTarget.value
    ) {
      this.checkOutTarget.value = "";
    }
  }

  /**
   * Sets the check-out field's min to the day after check-in (or the day
   * after tomorrow, if check-in hasn't been chosen yet).
   */
  updateCheckOutMin() {
    const minCheckIn = this.checkInTarget.value || this.tomorrowISO();

    this.checkOutTarget.min = this.dayAfterISO(minCheckIn);
  }

  /**
   * Returns tomorrow's date as an ISO 8601 string (YYYY-MM-DD), which is the
   * format expected by <input type="date">.
   */
  tomorrowISO() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return this.toISO(tomorrow);
  }

  /**
   * Returns the day after the given ISO date string, as an ISO date string.
   */
  dayAfterISO(isoDate) {
    const date = new Date(`${isoDate}T00:00:00`);
    date.setDate(date.getDate() + 1);
    return this.toISO(date);
  }

  toISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
